import {useEffect,useState} from 'react'
import {useSelector,useDispatch} from 'react-redux'
import {
	getClasses,
	updateEditor,
	registerForFree,
	selectPastClasses,
	getPreviousClasses,
	getUpcomingClasses,
	selectUpcomingClasses
} from './liveClassesSlice'
import {
	envAwarePost,
	copyToClipboard,
} from '../../util/util'
import {
	getPriceText
} from './helpers'
import useWindowSize from '../../hooks/useWindowSize'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import Card from 'react-bootstrap/Card'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Tooltip from 'react-bootstrap/Tooltip'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import useStripeAccount from '../../hooks/useStripeAccount'
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js'
import ClassCard from './ClassCard/ClassCard'
import ClassEditor from './ClassEditor/ClassEditor'
import NotifyModal from './NotifyModal/NotifyModal'
import RegisterModal from './RegisterModal/RegisterModal'
import ParticipantsModal from './ParticipantsModal/ParticipantsModal'
import { Link, useHistory } from 'react-router-dom';

import styles from './LiveClasses.module.css'

const LiveClasses = ()=>{
	const history = useHistory()
	const urlParams = new URLSearchParams(window.location.search)
	const [filterId,setFilterId] = useState(parseInt(urlParams.get('class_id')))
	const dispatch = useDispatch()
	const user = useSelector(state=>state.users.current_user)
	const canShowOnFTG = user.can_show_on_ftg

	useEffect(()=>{
		dispatch(getUpcomingClasses())
		//only fetch previous classes for trainers that have payments set up
		if(
			user.account_type !== 'client' &&
			user.stripe_account.enabled === 1
		){
			dispatch(getPreviousClasses())
		}
	},[dispatch])
	
	const upcomingClasses = useSelector(selectUpcomingClasses)
	const pastClasses = useSelector(selectPastClasses)
	const status = useSelector(state=>state.liveClasses.status)
	const upcomingStatus = useSelector(state=>state.liveClasses.upcomingStatus)
	const previousStatus = useSelector(state=>state.liveClasses.previousStatus)

	const hasPaymentsSetup =useSelector(state=>state.users.current_user.stripe_account?.enabled)
	const isPremiumAccount =useSelector(state=>parseInt(state.users.current_user.premium)>=2)
	
	const windowSize = useWindowSize()
	//grid || list
	const [layoutType,setLayoutType] = useState('list')
	const layoutOptionThreshold = 1000
	//effect to force grid layout when window size less then
	//layout option threshold
	useEffect(()=>{
		if( !windowSize[0] ) return
		if( windowSize[0] < layoutOptionThreshold ) setLayoutType('grid')
	},[windowSize])


	/*----------------------------------
		Logic for paid registrations
	----------------------------------*/

	const [showRegisterModal,setShowRegisterModal] = useState(false)
	const [selectedClass,setSelectedClass] = useState(null)

	const getRegisterPaymentIntent = (class_id)=>(
		envAwarePost(
			'api/classes/api/',
			{action:'getRegisterPaymentIntent',class_id}
		)
	)

	const [loadingCheckout,setLoadingCheckout] = useState(false)
	const [clientSecret,setClientSecret] = useState(null)
	useEffect(async ()=>{
		if(!selectedClass) return 
		setLoadingCheckout(true)
		const res = await getRegisterPaymentIntent(selectedClass._id)
		if(res) setLoadingCheckout(false)
		setClientSecret(res.data)
	},[selectedClass])

	const openRegisterModal = liveClass => {
		setSelectedClass(liveClass)
		setShowRegisterModal(true)
	}

	const closeRegisterModal = () => {
		setSelectedClass(null)
		setShowRegisterModal(false)
	}

	const stripePromise = useStripeAccount(user?.trainer_stripe_account)
	
	if (!user) return null

	const upcomingClassesComponent = (
		<div className={[
			styles.ClassList,
			layoutType==='grid'?styles.Grid:styles.List
		].join(' ')}>
			{
				upcomingStatus === 'pending' ?
				<Spinner animation="border" variant="primary"/>
				:
				(
					upcomingClasses.length === 0 ?
					<div>No upcoming classes</div>
					:
					upcomingClasses.filter(c=>!Boolean(filterId)||parseInt(c._id)===filterId).map( c => (
						<ClassCard 
							liveClass={c} 
							layoutType={layoutType}
							openRegisterModal={openRegisterModal}
							loadingCheckout={loadingCheckout}
						/>
					))
				)
			}
		</div>
	)

	const pastClassesComponent = (
		<div className={[
			styles.ClassList,
			layoutType==='grid'?styles.Grid:styles.List
		].join(' ')}>
			{
				previousStatus === 'pending' ?
				<Spinner animation="border" variant="primary"/>
				:
				(
					pastClasses.length === 0 ?
					<div>No previous classes</div>
					:
					pastClasses.filter(c=>!Boolean(filterId)||c._id===filterId).map( c => (
						<ClassCard 
							isPrevious
							liveClass={c} 
							layoutType={layoutType}
							openRegisterModal={openRegisterModal}
						/>
					))
				)
			}
		</div>
	)

	const trainerListComponent = (
		<Tabs id="trainerListComponent" defaultActiveKey="upcoming" variant="pills"  >
			<Tab eventKey="upcoming" title="Upcoming" size="sm" >
				{upcomingClassesComponent}
			</Tab>
			<Tab eventKey="previous" title="Previous" size="sm" >
				{pastClassesComponent}
			</Tab>
		</Tabs>
	)

	const classListComponent = (
		user.account_type === 'client' ?
		upcomingClassesComponent :
		trainerListComponent
	)

	const owner_id = Boolean(parseInt(user.gym_id)) ? user.gym_id : user.user_id_number

	if(!isPremiumAccount && user.account_type !== 'client'){
		return (
			<div id="containerDiv">
				<h1 style={{lineHeight:'1.5'}}>Live Video Classes</h1>
				<hr/>
				<h3>You must upgrade to a premium account and then set up payments to use the live video class feature. </h3>
				<Button
					variant='link'
					style={{padding:0}}
					as={Link}
					to={`/upgrade`}
				> Upgrade</Button>
			</div>
		)
	}else if(!hasPaymentsSetup && user.account_type !== 'client'){
		return (
			<div id="containerDiv">
				<h1 style={{lineHeight:'1.5'}}>Live Video Classes</h1>
				<hr/>
				<h3>You must set up payments to use the live video class feature. </h3>
				<Button
					variant='link'
					style={{padding:0}}
					as={Link}
					to={`/paymentsDashboard`}
				> Payments Page</Button>
			</div>
		)
	}
	return (
		<>
			<div className="pt-2 px-2 px-md-3" id="containerDiv">
				<strong style={{lineHeight:'1.5'}}>Live Video Classes</strong>
				<div className="d-block d-md-none">
					{
						user.account_type === 'client'?
						<p>Live video classes that your trainer offers will be here. They can be 1:1 sessions or group training.</p>
						:
						<p>Create live video training classes (max 200 participants)</p>
					}
				</div>
				<div className="d-none d-md-block">
					{
						user.account_type === 'client'?
						<p>Live video classes that your trainer offers will be here. They can be 1:1 sessions or group training.</p>
						:
						<p>Classes allow you to create live video training classes for 1:1 training, group training, or large classes. You can charge for these classes or offer them for free. All classes have a maximum size of 200 participants.</p>
					}
				</div>
				{!canShowOnFTG&& user.account_type !== 'client'&&
					<Alert variant="danger" className="text-dark">
						To host public classes on <a href="https://www.findtraingain.com" target="_blank" rel="noopener">FindTrainGain.com</a>
						<OverlayTrigger overlay={
							<Tooltip>
								FindTrainGain is a site that helps potential clients find Personal Trainers. To be visible on findtraingain.com's trainer search, make sure to fill out the 'Headline' section and the rest of your profile
							</Tooltip>
						}>
							<i className="ml-1 fas fa-info-circle" style={{ marginTop: "5px" }} />
						</OverlayTrigger>. 
						You need at least a headline set on <Link to='/profile'>your profile</Link>.
					</Alert>
				}
				<hr/>
				
				<div>
				<div className="mb-2" style={{display:'flex', justifyContent:'flex-end'}}>
				{windowSize[0] >= layoutOptionThreshold &&
					<>
						<span className="mr-1">pick layout:</span>
							<OverlayTrigger overlay={
								<Tooltip>Display classes in a grid layout</Tooltip>
							}>
								<Button 
									variant={layoutType==='grid'?'primary':'outline-primary'}
									onClick={()=>{setLayoutType('grid')}}
									className="mr-1" 
									size="sm"
								>
									<i className="fas fa-th"/>
								</Button>
							</OverlayTrigger>
							<OverlayTrigger overlay={
								<Tooltip>Display classes in a list layout</Tooltip>
							}>
								<Button 
									variant={layoutType==='list'?'primary':'outline-primary'}
									onClick={()=>{setLayoutType('list')}} 
									size="sm"
								>
									<i className="fas fa-th-list"/>
								</Button>
							</OverlayTrigger>
						</>
					}
					{ (user.account_type !== 'client') &&
						<>
								<Button onClick={()=>{dispatch(updateEditor({show:true}))}} size="sm" variant="outline-success" style={{marginLeft:'10px'}}>
									New Live Class
								</Button>
								{canShowOnFTG&&
									<OverlayTrigger overlay={
										<Tooltip>A link that shows your public classes on Find Train Gain</Tooltip>
									}>
										<Button 
											onClick={()=>{
												copyToClipboard(`https://findtraingain.com/live-classes-search-results.php?trainer=${owner_id}`)
											}}
											variant="outline-primary" 
											className="ml-2" 
											size="sm"
										>
											Copy Link To Public Classes
										</Button>
									</OverlayTrigger>
								}
						</>
					}
				</div>

				</div>
				{Boolean(filterId)&&
					<div className="mb-2">
						Viewing filtered list: 
						<Button 
							onClick={()=>{ 
								setFilterId(false)
								history.replace('/classes/')
							}}
							variant="outline-danger" 
							className="ml-2"
							size="sm"
						>
							Clear Filters
						</Button>
					</div>
				}
				{classListComponent}
				<hr/>
				<div>
					<a 
						href="https://www.fitsw.com/help/for-trainers/other-trainers/how-to-get-the-most-out-of-live-classes/"
						target="_blank"
					>
						Having trouble with streaming quality?
					</a>
				</div>
			</div>
			<ParticipantsModal/>
			<ClassEditor/>
			<NotifyModal/>
			{(Boolean(stripePromise)&&Boolean(clientSecret))&&
				<Elements stripe={stripePromise} options={{clientSecret}}>
	                <ElementsConsumer>
	                    {({ elements, stripe }) => (
							<RegisterModal 
								selectedClass={selectedClass}
								onHide={closeRegisterModal}
								show={showRegisterModal}
								elements={elements}
	                            stripe={stripe}
							/>
						)}
					</ElementsConsumer>
				</Elements>
			}
		</>
	)
}

export default LiveClasses